import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { TextRecognition } from '@capacitor-mlkit/text-recognition';

/**
 * On-device text recognition, task: Capacitor + on-device OCR before
 * FS/TEC (2026-09-08). Runs against the same already-captured, already-
 * compressed photo Blob the online scan would have used, never a second
 * capture, so a rep only ever takes one photo regardless of what happens
 * to it next.
 *
 * Only ever called from BadgeScanner.jsx's existing offline branch
 * (err.status === 0), and only ever reachable there when
 * Capacitor.isNativePlatform() is true: @capacitor-mlkit/text-recognition
 * has no real web implementation, its own web.d.ts throws an
 * "unimplemented" exception unconditionally, so this must never run in
 * the browser. The platform check here is a second, redundant guard
 * against that, not the only one.
 *
 * ML Kit's processImage() takes a filesystem path, not a Blob, so the
 * photo is written to a scratch file in the cache directory first and
 * deleted again once read, success or failure, rather than left behind.
 *
 * Returns { text, blocks } on a genuine completion (blocks may be an
 * empty array if the model found nothing legible), or null if the call
 * itself never produced a real answer (no camera-cache write, no native
 * bridge, an actual plugin exception). null deliberately does not mean
 * "no text was found", the same distinction badge_ocr_attempted_at
 * already draws elsewhere in this app: a genuine completion is stamped
 * either way, an infrastructure failure never is, so a phone with a
 * flaky filesystem write does not get recorded as a confident "nothing
 * on this badge" when the model was never actually asked.
 */
export async function runOnDeviceOcr(blob) {
  if (!Capacitor.isNativePlatform()) return null;

  const fileName = `ocr-scratch-${Date.now()}.jpg`;
  try {
    const base64 = await blobToBase64(blob);
    await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path: fileName, directory: Directory.Cache });
    return await TextRecognition.processImage({ path: uri });
  } catch (err) {
    console.error('On-device OCR failed:', err);
    return null;
  } finally {
    try {
      await Filesystem.deleteFile({ path: fileName, directory: Directory.Cache });
    } catch {
      // Scratch file cleanup failing is not worth surfacing; the cache
      // directory is the OS's own to reclaim regardless.
    }
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
